import {
    Box,
    Button,
    Card,
    Container,
    Stack,
    TextField,
    Typography,
    Alert,
} from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "@emotion/styled";
import { PRIVATE_LOGIN_ORIGIN } from "src/config";

const StyledCard = styled(Card)`
  background-color: #0D0E10;
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 1.5rem;
  padding: 3rem;
  box-shadow: 0 20px 40px 0 rgba(0, 0, 0, 0.4);
`;

const GradientButton = styled(Button)`
  background: linear-gradient(135deg, #00ffa2 0%, #00bcff 100%);
  color: #000;
  font-weight: 800;
  border-radius: 0.75rem;
  padding: 0.8rem;
  &:hover {
    background: linear-gradient(135deg, #00e08f 0%, #00a8e6 100%);
    box-shadow: 0 0 15px rgba(0, 255, 162, 0.4);
  }
`;

const StyledTextField = styled(TextField)`
  & .MuiOutlinedInput-root {
    border-radius: 0.75rem;
    background-color: rgba(255, 255, 255, 0.05);
    color: #fff;
    & fieldset {
      border-color: rgba(255, 255, 255, 0.1);
    }
    &:hover fieldset {
      border-color: rgba(255, 255, 255, 0.3);
    }
    &.Mui-focused fieldset {
      border-color: #00ffa2;
    }
  }
  & .MuiInputLabel-root {
    color: rgba(255, 255, 255, 0.5);
    &.Mui-focused {
      color: #00ffa2;
    }
  }
`;

const AdminLogin = () => {
    const [step, setStep] = useState(0); // 0: Login, 1: 2FA
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [twoFactorCode, setTwoFactorCode] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const navigate = useNavigate();

    const handleLogin = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        
        try {
            const response = await fetch(`${PRIVATE_LOGIN_ORIGIN}/api/login`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Accept": "application/json",
                },
                body: JSON.stringify({
                    email: username.trim(),
                    password: password,
                    remember: true
                }),
            });

            const data = await response.json();

            if (response.ok || data.token || data.access_token) {
                const token = data.token || data.access_token || data.btc?.token;
                if (token) {
                    localStorage.setItem("admin-token", token);
                    navigate("/admin/dashboard");
                } else if (data.message === "google 2fa enabled" || data.status === false) {
                    setStep(1);
                } else {
                    setError("Unexpected response format from server.");
                }
            } else if (data.message === "google 2fa enabled") {
                // Some APIs return 401 but with the message "google 2fa enabled"
                setStep(1);
            } else {
                setError(data.message || "Invalid administrator credentials (401)");
            }
        } catch (err) {
            console.error("Login error:", err);
            setError("Server connection failed. Please try again later.");
        } finally {
            setLoading(false);
        }
    };

    const handleVerify2FA = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);

        try {
            const response = await fetch(`${PRIVATE_LOGIN_ORIGIN}/api/twofaverify`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Accept": "application/json",
                },
                body: JSON.stringify({
                    email: username.trim(),
                    password: password,
                    remember: true,
                    key: null,
                    verify: 1,
                    code: twoFactorCode
                }),
            });

            const data = await response.json();

            if ((response.ok || data.status === true) && (data.token || data.access_token || data.btc?.token)) {
                const token = data.token || data.access_token || data.btc?.token;
                localStorage.setItem("admin-token", token);
                navigate("/admin/dashboard");
            } else {
                setError(data.message || "Invalid 2FA code. Please try again.");
            }
        } catch (err) {
            console.error("2FA error:", err);
            setError("Authentication failed. Please check your connection.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box 
            sx={{ 
                minHeight: "100vh", 
                display: "flex", 
                alignItems: "center", 
                justifyContent: "center",
                pt: "80px", 
                background: "#0D0E10"
            }}
        >
            <Container maxWidth="xs">
                <StyledCard>
                    <Stack spacing={4}>
                        <Box sx={{ textAlign: "center" }}>
                            <Typography variant="h4" fontWeight="900" sx={{ color: "#fff", mb: 1 }}>
                                Admin Portal
                            </Typography>
                            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.6)" }}>
                                Unauthorized access is strictly prohibited
                            </Typography>
                        </Box>

                        {error && (
                            <Alert severity="error" sx={{ borderRadius: "0.5rem" }}>
                                {error}
                            </Alert>
                        )}

                        {step === 0 ? (
                            <form onSubmit={handleLogin}>
                                <Stack spacing={3}>
                                    <StyledTextField
                                        fullWidth
                                        id="admin-username"
                                        name="email"
                                        label="Admin Username / Email"
                                        variant="outlined"
                                        value={username}
                                        onChange={(e) => setUsername(e.target.value)}
                                        required
                                        disabled={loading}
                                    />
                                    <StyledTextField
                                        fullWidth
                                        id="admin-password"
                                        name="password"
                                        label="Secret Key (Password)"
                                        type="password"
                                        variant="outlined"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        onKeyDown={(e) => e.key === 'Enter' && handleLogin(e)}
                                        required
                                        disabled={loading}
                                        helperText={password.length > 0 ? `Character count: ${password.length}` : ""}
                                        FormHelperTextProps={{ sx: { color: 'rgba(255,255,255,0.3)' } }}
                                    />
                                    <GradientButton fullWidth type="submit" variant="contained" disabled={loading}>
                                        {loading ? "Authenticating..." : "Authenticate"}
                                    </GradientButton>
                                </Stack>
                            </form>
                        ) : (
                            <form onSubmit={handleVerify2FA}>
                                <Stack spacing={3}>
                                    <Box sx={{ textAlign: 'center', mb: 1 }}>
                                        <Typography variant="body2" sx={{ color: '#00ffa2', fontWeight: 700 }}>
                                            2FA Required
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>
                                            Enter the code from your authenticator app
                                        </Typography>
                                    </Box>
                                    <StyledTextField
                                        fullWidth
                                        label="6-Digit Code"
                                        variant="outlined"
                                        value={twoFactorCode}
                                        onChange={(e) => setTwoFactorCode(e.target.value)}
                                        required
                                        disabled={loading}
                                        inputProps={{ maxLength: 6, style: { textAlign: 'center', letterSpacing: '0.5rem', fontSize: '1.5rem' } }}
                                        autoFocus
                                    />
                                    <GradientButton fullWidth type="submit" variant="contained" disabled={loading}>
                                        {loading ? "Verifying..." : "Verify & Enter"}
                                    </GradientButton>
                                    <Button 
                                        onClick={() => setStep(0)} 
                                        sx={{ color: 'rgba(255,255,255,0.3)', textTransform: 'none' }}
                                        disabled={loading}
                                    >
                                        Back to login
                                    </Button>
                                </Stack>
                            </form>
                        )}
                    </Stack>
                </StyledCard>
            </Container>
        </Box>
    );
};

export default AdminLogin;
